import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft } from "lucide-react";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL!;

const quickAmounts = [25000, 50000, 75000, 100000, 200000, 500000];

const DonatePage = () => {
  const { campaignId, slug } = useParams();
  const navigate = useNavigate();
  const addToCartSent = useRef(false);
  const [amount, setAmount] = useState<number>(25000);
  const [donorName, setDonorName] = useState("");
  const [donorPhone, setDonorPhone] = useState("");
  const [donorEmail, setDonorEmail] = useState("");
  const [note, setNote] = useState("");
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [loading, setLoading] = useState(false);
  const [amountError, setAmountError] = useState("");

  // 🔥 Handle anonymous auto replace
  useEffect(() => {
    if (isAnonymous) {
      setDonorName("Orang Baik");
    } else {
      setDonorName("");
    }
  }, [isAnonymous]);

  // 🔥 Validasi nominal
  useEffect(() => {
    if (amount < 10000) {
      setAmountError("Minimal donasi Rp 10.000");
    } else {
      setAmountError("");
    }
  }, [amount]);

  const isFormValid = amount >= 10000 && donorName.trim() !== "" && donorPhone.trim() !== "";

  const handleSubmit = async () => {
    if (!isFormValid) return;

    try {
      setLoading(true);

      const res = await axios.post(`${API_BASE_URL}/api/donations`, {
        campaign_id: Number(campaignId),
        name: donorName,
        email: donorEmail,
        phone: donorPhone,
        amount: amount,
        message: note,
      });

      if (res.data.success) {
        const donationId = res.data.data.donation_id;

        // 🔥 Redirect ke halaman payment
        navigate(`/payment/${donationId}`);
      }
    } catch (error) {
      console.error(error);
      alert("Terjadi kesalahan");
    } finally {
      setLoading(false);
    }
  };

  //pixel addtochart
  useEffect(() => {
    if (window.fbq && campaignId && !addToCartSent.current) {
      window.fbq("track", "AddToCart", {
        content_ids: [campaignId],
        content_type: "donation",
        currency: "IDR",
        value: amount,
      });

      addToCartSent.current = true;
    }
  }, [campaignId]);

  return (
    <div className="bg-gray-100 font-signika">
      <div className="max-w-2xl mx-auto bg-white min-h-screen p-5">
        {/* 🔙 BACK BUTTON */}

        <div className="flex justify-start items-center gap-3 mb-8">
          <button onClick={() => navigate(`/donasi/${slug}`)} className="flex items-center gap-2 ">
            <ArrowLeft size={20} />
          </button>

          <div>
            <h1 className="text-lg font-semibold ">Isi Nominal Donasi</h1>
            <p>{slug?.replace(/-/g, " ")}</p>
          </div>
        </div>

        {/* QUICK NOMINAL */}
        <div className="mb-6">
          <div className="font-medium mb-3">Pilih Nominal Cepat</div>
          <div className="grid grid-cols-3 gap-3">
            {quickAmounts.map((item) => (
              <button
                key={item}
                onClick={() => setAmount(item)}
                className={`py-3 rounded-xl border text-sm font-medium transition
                ${amount === item ? "bg-primary2 text-white border-primary2" : "bg-gray-50 border-gray-200"}`}>
                {item / 1000}rb
              </button>
            ))}
          </div>
        </div>

        {/* NOMINAL LAINNYA */}
        <div className="mb-2">
          <div className="font-medium mb-2">Nominal Lainnya</div>
          <div className="flex items-center bg-gray-100 rounded-xl px-4 py-3">
            <span className="text-gray-500 mr-2">Rp</span>
            <input type="number" value={amount} onChange={(e) => setAmount(Number(e.target.value))} className="bg-transparent outline-none w-full" />
          </div>

          {amountError && <div className="text-red-500 text-xs mt-1">{amountError}</div>}
        </div>

        <hr className="my-6" />

        {/* IDENTITAS */}
        <div className="mb-6">
          <div className="font-medium mb-3">Identitas Donatur</div>

          <input type="text" placeholder="Nama Lengkap" value={donorName} disabled={isAnonymous} onChange={(e) => setDonorName(e.target.value)} className="w-full mb-3 bg-gray-100 rounded-xl px-4 py-3 outline-none" />

          <input type="text" placeholder="No. WhatsApp (08xx...)" value={donorPhone} onChange={(e) => setDonorPhone(e.target.value)} className="w-full mb-3 bg-gray-100 rounded-xl px-4 py-3 outline-none" />

          <input type="email" placeholder="Email (Opsional)" value={donorEmail} onChange={(e) => setDonorEmail(e.target.value)} className="w-full mb-3 bg-gray-100 rounded-xl px-4 py-3 outline-none" />

          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={isAnonymous} onChange={() => setIsAnonymous(!isAnonymous)} />
            Sembunyikan nama saya (Orang Baik)
          </label>
        </div>

        {/* DOA */}
        <div>
          <div className="font-medium mb-2">Doa & Dukungan</div>
          <textarea placeholder="Tulis doa baikmu di sini (opsional)" value={note} onChange={(e) => setNote(e.target.value)} className="w-full bg-gray-100 rounded-xl px-4 py-3 outline-none h-24 resize-none" />
        </div>

        {/* BUTTON */}
        <div className="mt-6">
          <button
            onClick={handleSubmit}
            disabled={!isFormValid || loading}
            className={`w-full py-4 rounded-xl font-semibold text-base transition
            ${!isFormValid ? "bg-gray-300 text-gray-500 cursor-not-allowed" : "text-white bg-gradient-to-r from-[#1b6f8f] to-primary2 shadow-[0_0_20px_#2596be]"}`}>
            {loading ? "Memproses..." : "LANJUT PEMBAYARAN →"}
          </button>
        </div>

        <p className="text-xs text-gray-400 mt-3 text-center">Pembayaran aman & terverifikasi otomatis.</p>
      </div>
    </div>
  );
};

export default DonatePage;
